import { T } from './theme';
// DictatePanel — shared dictation capture for every note surface.
// Live words via WSS -> /ws/emr/transcribe (Deepgram relay, audio in flight only,
// never stored). If streaming yields nothing (Safari/mp4, bad wifi), the recorded
// buffer falls back to POST /api/emr/transcribe. Text is editable before drafting.
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API = 'https://tcio-api.onrender.com';
const WS_BASE = 'wss://tcio-api.onrender.com';
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : 'transparent',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });

export default function DictatePanel({ onSubmit, busy, result, cta, submitLabel, placeholder, fallbackPath = '/api/emr/transcribe' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [rec, setRec] = useState(false);
  const [interim, setInterim] = useState('');
  const [micMsg, setMicMsg] = useState('');
  const wsRef = useRef(null);
  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const gotWordsRef = useRef(false);
  const mimeRef = useRef('');
  const liveDeadRef = useRef(false);   // 10/24 (Dax 260a8d38): live stream was interrupted at some point -> full recording is transcribed on stop
  const preTextRef = useRef('');       // text in the box before this recording started
  const stoppingRef = useRef(false);
  const reconnectsRef = useRef(0);

  useEffect(() => () => hardStop(), []); // unmount cleanup

  const token = () => (axios.defaults.headers.common['Authorization'] || '').replace(/^Bearer\s+/i, '');

  function hardStop() {
    try { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); } catch (e) {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    try { if (wsRef.current) wsRef.current.close(); } catch (e) {}
    mrRef.current = null; streamRef.current = null; wsRef.current = null;
    setRec(false); setInterim('');
  }

  async function startMic() {
    setMicMsg('');
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) { setMicMsg('Microphone access denied — you can still type or paste.'); return; }
    streamRef.current = stream;
    chunksRef.current = [];
    gotWordsRef.current = false;
    liveDeadRef.current = false; stoppingRef.current = false; reconnectsRef.current = 0; preTextRef.current = text;

    const mime = (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
      ? 'audio/webm;codecs=opus'
      : (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/mp4')) ? 'audio/mp4' : '';
    mimeRef.current = mime || 'audio/webm';
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    mrRef.current = mr;

    const openWs = (isReconnect) => {
      let ws;
      try { ws = new WebSocket(WS_BASE + '/ws/emr/transcribe?token=' + encodeURIComponent(token())); }
      catch (e) { wsRef.current = null; liveDeadRef.current = true; return; }
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      ws.onopen = () => { if (isReconnect && chunksRef.current[0]) chunksRef.current[0].arrayBuffer().then(b => { try { ws.send(b); } catch (e) {} }); if (isReconnect) setMicMsg('Live words are back. The full recording is still transcribed when you stop.'); };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === 'transcript') {
            if (m.text) gotWordsRef.current = true;
            if (m.is_final) {
              if (m.text) setText(t => (t ? t.replace(/\s+$/, '') + ' ' : '') + m.text);
              setInterim('');
            } else setInterim(m.text || '');
          } else if (m.type === 'reconnecting') setMicMsg('Live words paused for a moment - keep talking, nothing is lost.');
          else if (m.type === 'reconnected' || m.type === 'ready') { if (m.type === 'reconnected') setMicMsg(''); }
          else if (m.type === 'error') { liveDeadRef.current = true; setMicMsg('Live words are off - keep going; the full recording is transcribed when you stop.'); }
        } catch (e) {}
      };
      ws.onclose = () => {
        if (stoppingRef.current) return;
        liveDeadRef.current = true;
        if (reconnectsRef.current < 5) { reconnectsRef.current += 1; setMicMsg('Live words dropped - reconnecting. Keep going; the full recording is transcribed when you stop.');
          setTimeout(() => { if (!stoppingRef.current && mrRef.current && mrRef.current.state === 'recording') openWs(true); }, 800); }
        else setMicMsg('Live words are off - keep going; the full recording is transcribed when you stop.');
      };
    };
    if (/mp4/.test(mimeRef.current)) { liveDeadRef.current = true; setMicMsg('Recording. This browser does not stream live words; the whole session is transcribed when you stop.'); }
    else openWs(false);

    mr.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      chunksRef.current.push(e.data);
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        e.data.arrayBuffer().then(b => { try { ws.send(b); } catch (e2) {} });
      }
    };

    mr.onstop = async () => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) { try { ws.send('stop'); } catch (e) {} }
      setTimeout(() => { try { if (ws) ws.close(); } catch (e) {} }, 1500);
      // Full-recording transcription whenever the live stream produced nothing OR was interrupted at any point (Dax 260a8d38).
      if ((!gotWordsRef.current || liveDeadRef.current) && chunksRef.current.length) {
        const replaceLive = liveDeadRef.current;
        setMicMsg('Transcribing the full recording…');
        try {
          const blob = new Blob(chunksRef.current, { type: mimeRef.current });
          const r = await axios.post(API + fallbackPath, blob,
            { headers: { 'Content-Type': mimeRef.current } });
          if (r.data && r.data.transcript) {
            if (replaceLive) setText((preTextRef.current ? preTextRef.current.replace(/\s+$/, '') + ' ' : '') + r.data.transcript);
            else setText(t => (t ? t.replace(/\s+$/, '') + ' ' : '') + r.data.transcript);
            setMicMsg('');
          } else setMicMsg('No speech detected.');
        } catch (e) {
          setMicMsg('Transcription failed — you can type or paste instead.');
        }
      }
      chunksRef.current = [];
    };

    mr.start(250);
    setRec(true);
  }

  function stopMic() {
    stoppingRef.current = true;
    try { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); } catch (e) {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    setRec(false); setInterim('');
  }

  if (!open) return (
    <button style={{ ...btn(true), width:'100%' }} onClick={() => setOpen(true)}>
      {cta || '🎙 Dictate → AI drafts'}</button>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
        <div style={label}>Session dictation</div>
        <button onClick={rec ? stopMic : startMic} disabled={busy}
          style={{ ...btn(!rec), padding:'4px 12px', fontSize:12,
            background: rec ? T.blocking : T.rose, color:'#fff', border:'none' }}>
          {rec ? '⏹ Stop' : '🎙 Record'}</button>
      </div>
      <textarea style={{ ...input, minHeight:130, fontSize:12.5 }} value={text}
        onChange={e => setText(e.target.value)}
        placeholder={placeholder || 'Tap Record and speak — words appear here as you talk. Or type/paste. Nothing is signed.'} />
      {(rec || interim) && (
        <div style={{ fontSize:12, color:T.inkSoft, fontStyle:'italic', marginTop:4, minHeight:16 }}>
          {rec && <span style={{ color:T.blocking, fontWeight:600, fontStyle:'normal' }}>● </span>}
          {interim || (rec ? 'listening…' : '')}</div>
      )}
      {micMsg && <div style={{ fontSize:12, color:T.blocking, marginTop:4 }}>{micMsg}</div>}
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button style={{ ...btn(true), flex:1 }} disabled={busy || rec || !text.trim()}
          onClick={() => onSubmit(text)}>
          {busy ? 'Drafting…' : (submitLabel || 'Draft all notes')}</button>
        <button style={btn(false)} disabled={busy} onClick={() => { hardStop(); setOpen(false); }}>✕</button>
      </div>
      {result || null}
    </div>
  );
}
